import type { ModifyAssetPaymentParams } from '@repo/types/asset-payment';
import {
    type DeleteAttachmentParams,
    type UpdateAttachmentCommentParams,
    type UploadAttachmentParams,
} from '@repo/types/attachment';
import { type GetBarCodeResponse, isGetBarCodeResponse } from '@repo/types/barcode';
import { isDraft } from '@repo/types/draft';
import { type CancelOrderReasonCode, isOrder } from '@repo/types/order';
import { isDefaultPrinter, isFavouritePrinterListResponse, isGetPrinterResponse } from '@repo/types/printer';
import { isQuotation } from '@repo/types/quotation';
import { ApiError, getCountryLanguage } from '@repo/utils';

import { getServiceEndpoint } from '@/api/helpers.api';
import { fetcher } from '@/utils/fetch.utils';
import { getAppConfig } from '@/utils/window.utils';

import { AssetRetrievalError } from './asset-retrieval-error.api';

const { country, language } = getCountryLanguage();

export interface SendConfirmationPayload {
    orderNo: string;
    orderNoSource: string | undefined;
    buCode: string;
    isDraftOrder: boolean;
    isQuotation: boolean;
    locale: string;
    // print
    noOfCopies?: number;
    printerName?: string;
    // email or sms
    customerContactValue?: string;
    hasTermsAndConditions?: boolean;
    includePaymentLink?: boolean;
    hasReservation?: boolean;
}

export const assetShareOption = {
    EMAIL: 'EMAIL',
    PRINT: 'PRINT',
    SMS: 'SMS',
} as const;

export const orderShareOption = {
    EMAIL: 'email',
    PRINT: 'print',
    SMS: 'sms',
} as const;

export const shareEvent = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
} as const;

export type OrderShareType = (typeof orderShareOption)[keyof typeof orderShareOption];
export type ShareEventType = (typeof shareEvent)[keyof typeof shareEvent];
export type AssetShareTypeArray = Array<(typeof assetShareOption)[keyof typeof assetShareOption]>;

export type AssetShareType = (typeof assetShareOption)[keyof typeof assetShareOption];

export interface SendMessagePayload {
    emailAddress?: string | undefined;
    phoneNumber?: string;
    printer?: string | undefined;
    printerServer?: string | undefined;
    noOfCopies?: number | undefined;
    sharingType: AssetShareTypeArray;
    locale?: string;
    entityType: 'QUOTATION' | 'DRAFT';
    eventType: ShareEventType;
    entityId: string;
}

const retrieveAssetById = async (url: string, assetId?: string): Promise<unknown> => {
    try {
        if (!assetId) {
            throw new ApiError('Something went wrong', undefined, {
                errorCode: 'UNDEFINED',
            });
        }

        const response = await fetcher(`${url}/${assetId}`);
        return await response.json();
    } catch (error: unknown) {
        if (error instanceof ApiError) {
            throw new AssetRetrievalError(error);
        }

        throw error;
    }
};

export const getOrder = async (assetId?: string) => {
    const config = getAppConfig();
    const data = await retrieveAssetById(
        getServiceEndpoint(config.api.services.orderTaking, 'getOrderDetails'),
        assetId
    );

    return isOrder(data) ? data : Promise.reject(new AssetRetrievalError());
};

export const getDraft = async (assetId?: string) => {
    const config = getAppConfig();
    const data = await retrieveAssetById(
        getServiceEndpoint(config.api.services.orderTaking, 'getDraftDetails'),
        assetId
    );

    return isDraft(data) ? data : Promise.reject(new AssetRetrievalError());
};

export const getQuotation = async (assetId?: string) => {
    const config = getAppConfig();
    const data = await retrieveAssetById(
        getServiceEndpoint(config.api.services.orderTaking, 'getQuotationDetails'),
        assetId
    );

    return isQuotation(data) ? data : Promise.reject(new AssetRetrievalError());
};

export const deleteDraft = async (assetId?: string) => {
    const config = getAppConfig();

    const url = config.feature.draftV2Service?.enable
        ? `${getServiceEndpoint(config.api.services.orderTaking, 'deleteDraft')}/${assetId}`
        : `${getServiceEndpoint(config.api.services.classicSalja, 'deleteDraft')}/${assetId}`;

    return await fetcher(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'CANCELED' }),
    });
};

export const deleteQuotation = async (assetId?: string) => {
    const config = getAppConfig();
    const url = `${getServiceEndpoint(config.api.services.orderTaking, 'deleteQuotation')}/${assetId}`;
    return await fetcher(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'CANCELED' }),
    });
};

export const cancelOrder = async (payload: {
    orderId: string;
    orderNoSource?: string | undefined;
    cashAndCarryOrder?: boolean | undefined;
    reasonCode?: CancelOrderReasonCode | undefined;
    buCode?: string | undefined;
    orderType?: string | undefined;
}) => {
    const config = getAppConfig();
    const modifyOrderUrl = getServiceEndpoint(config.api.services.classicSalja, 'modifyOrder');
    const url = payload.orderType === 'ISOM' ? `${modifyOrderUrl}/cancel-order` : `${modifyOrderUrl}/order/submit-form`;

    const body =
        payload.orderType === 'ISOM'
            ? {
                  orderNo: payload.orderId,
                  reasonCode: payload.reasonCode ?? 'CUSCHMIND',
                  orderNoSource: payload.orderNoSource,
              }
            : {
                  action: 'CANCEL_ORDER',
                  order: {
                      version: 0,
                      orderNo: payload.orderId,
                      orderNoSource: payload.orderNoSource,
                      cashAndCarryOrder: payload.cashAndCarryOrder,
                  },
                  businessUnit: {
                      code: payload.buCode,
                  },
                  formData: {
                      reasonCode: payload.reasonCode,
                  },
              };

    return await fetcher(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
};

export const downloadAttachment = async (fileName: string, fileLocation?: string) => {
    const config = getAppConfig();
    const performSapiOperation = fileLocation ? 'true' : 'false';
    const attachmentLocation = fileLocation ? `&attachmentLocation=${fileLocation}` : '';
    const baseUrl = `${getServiceEndpoint(config.api.services.classicSalja, 'downloadAttachment')}/${country}/${language}`;
    const params = `?fileName=${fileName}&performSapiOperation=${performSapiOperation}${attachmentLocation}`;
    const response = await fetcher(baseUrl + params);
    return await response.arrayBuffer();
};

const getAttachmentUrl = (orderNoSource: string, orderNo: string) => {
    const config = getAppConfig();
    return `${getServiceEndpoint(config.api.services.classicSalja, 'modifyOrder')}/isom-order-attachment/${orderNoSource}/${orderNo}`;
};

export const deleteAttachment = async ({
    fileReference,
    attachmentLocation,
    orderNo,
    orderNoSource,
}: DeleteAttachmentParams) => {
    const url = getAttachmentUrl(orderNoSource, orderNo);
    const attachment = { fileReference, attachmentLocation, isDeleted: true };
    return await fetcher(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attachments: [attachment] }),
    });
};

export const updateAttachmentComment = async ({
    fileReference,
    attachmentLocation,
    attachmentComment,
    orderNo,
    orderNoSource,
}: UpdateAttachmentCommentParams) => {
    const url = getAttachmentUrl(orderNoSource, orderNo);
    return await fetcher(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attachments: [{ fileReference, attachmentLocation, attachmentComment }] }),
    });
};

export const uploadAttachment = async ({ file, orderNumber, orderNoSource }: UploadAttachmentParams) => {
    const config = getAppConfig();
    const { maxSizeMb } = config.feature.attachments ?? {};

    if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
        throw new Error(`File size exceeds ${maxSizeMb}MB limit`);
    }

    const formData = new FormData();
    formData.append('file', file);
    const url = `${getServiceEndpoint(config.api.services.classicSalja, 'uploadAttachment')}/${country.toUpperCase()}/${language}?performSapiOperation=true&orderNumber=${orderNumber}&orderNoSource=${orderNoSource}`;
    return await fetcher(url, {
        method: 'POST',
        body: formData,
    });
};

export const getPrinters = async (buCode: string) => {
    const config = getAppConfig();
    const url = `${getServiceEndpoint(config.api.services.classicSalja, 'orderOutput')}/v2/printers/${buCode}`;
    const res = await fetcher(url);
    const data = await res.json();
    return isGetPrinterResponse(data) ? data : Promise.reject(new Error());
};

export const getFavouritePrinterList = async (buCode: string, mailNickname: string) => {
    const config = getAppConfig();
    const url = `${getServiceEndpoint(config.api.services.classicSalja, 'orderOutput')}/v2/printers/favoritePrinterList/${buCode}/${mailNickname}`;
    const res = await fetcher(url);
    const data = await res.json();
    return isFavouritePrinterListResponse(data) ? data : Promise.reject(new Error());
};

export const getDefaultPrinter = async (buCode: string, mailNickname: string) => {
    const config = getAppConfig();
    const url = `${getServiceEndpoint(config.api.services.classicSalja, 'orderOutput')}/v2/printers/defaultPrinter/${buCode}/${mailNickname}`;
    const res = await fetcher(url);
    const data = await res.json();
    return isDefaultPrinter(data) ? data : Promise.reject(new Error());
};

export const sendConfirmation = async (option: OrderShareType, payLoad: SendConfirmationPayload) => {
    const config = getAppConfig();
    const url = `${getServiceEndpoint(config.api.services.classicSalja, 'orderOutput')}/${option}/confirmation`;
    return await fetcher(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payLoad),
    });
};

export const sendMessage = async (payLoad: SendMessagePayload) => {
    const config = getAppConfig();
    const url = getServiceEndpoint(config.api.services.orderTaking, 'sendMessage');
    return await fetcher(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payLoad),
    });
};

export const getBarCode = async (orderId: string): Promise<GetBarCodeResponse> => {
    const config = getAppConfig();
    const url = `${getServiceEndpoint(config.api.services.classicSalja, 'orderRetrieval')}/${orderId}/barcode`;
    const res = await fetcher(url);
    const data = await res.json();
    return isGetBarCodeResponse(data) ? data : Promise.reject(new Error());
};

export const modifyOrderPaymentDate = async ({ orderId, orderNoSource, paymentDate }: ModifyAssetPaymentParams) => {
    const config = getAppConfig();
    const url = `${getServiceEndpoint(config.api.services.classicSalja, 'modifyOrderPaymentDate')}/${orderNoSource}/${orderId}`;

    // SAPI requirement is to send payment due date in UTC and with format: 'YYYY-MM-DD[T]HH:mm:ss'
    const paymentDateUtcSapiSupportedFormat = paymentDate.endsWith('Z') ? paymentDate.slice(0, -1) : paymentDate;

    return await fetcher(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentDate: paymentDateUtcSapiSupportedFormat }),
    });
};
